// src/hooks/useProctoringFlags.js
// Lekki proctoring: twarz (FaceDetector → BlazeFace fallback), alt-tab/blur,
// stan kamer/mikrofonu oraz VAD (hałas/rozmowa) przez WebAudio.
// Wysyła POST /api/exam/attempts/:id/flags z { type, clientTs, meta }.
// Wszystkie żądania z nagłówkiem Authorization.

import { useEffect, useRef } from 'react';

const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

const DEFAULT_COOLDOWNS = {
    face_missing:     20000,
    multi_face:       20000,
    alt_tab:          15000,
    window_blur:      15000,
    camera_off:       20000,
    camera_switched:  30000,
    mic_muted:        20000,
    noise:            20000,
    conversation:     25000,
};

function ensureScript(src, globalName){
    return new Promise((resolve, reject) => {
        if (globalName && window[globalName]) return resolve();
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing){
            const done = () => resolve();
            const fail = () => reject(new Error(`Load fail: ${src}`));
            existing.addEventListener('load', done, { once:true });
            existing.addEventListener('error', fail, { once:true });
            if (existing.getAttribute('data-loaded') === '1') return resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = () => { s.setAttribute('data-loaded','1'); resolve(); };
        s.onerror = () => reject(new Error(`Load fail: ${src}`));
        document.head.appendChild(s);
    });
}

export function useProctoringFlags({
                                       attemptId,
                                       enabled = true,
                                       faceIntervalMs = 1400,
                                       cooldowns = DEFAULT_COOLDOWNS,
                                       // VAD (hałas/rozmowa)
                                       noiseCalibMs = 3000,
                                       noiseMarginDb = 12,
                                       noiseMinHoldMs = 2000,
                                       speechBandHz = [300, 3400],
                                       speechMinHoldMs = 1500,
                                       vadPollMs = 120,
                                   }){
    const lastSentRef = useRef({});
    const streamRef   = useRef(null);
    const timersRef   = useRef({}); // detectLoop, vadLoop
    const audioRef    = useRef({ ctx:null, src:null, analyser:null, timeBuf:null, freqBuf:null });
    const calibRef    = useRef({ done:false, endTs:0, floorDb:-80 });
    const noiseState  = useRef({ startTs:0, peakDb:-Infinity });
    const speechState = useRef({ startTs:0 });

    async function sendFlag(type, meta){
        const now = Date.now();
        const cd  = Number(cooldowns?.[type] ?? 15000);
        if (lastSentRef.current[type] && now - lastSentRef.current[type] < cd) return;
        lastSentRef.current[type] = now;
        try{
            await fetch(`/api/exam/attempts/${attemptId}/flags`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type':'application/json', ...authHeader() },
                body: JSON.stringify({ type, clientTs: now, meta: meta || null }),
            });
        }catch{/* best effort */}
    }

    function rmsToDb(rms){ return 20 * Math.log10(Math.max(rms, 1e-8)); }

    useEffect(()=>{
        if (!enabled || !attemptId) return;

        let cancelled = false;
        let faceDetector = null;
        let useNative = false;

        const cleanup = ()=>{
            if (timersRef.current.detectLoop) clearTimeout(timersRef.current.detectLoop);
            if (timersRef.current.vadLoop)    clearTimeout(timersRef.current.vadLoop);
            timersRef.current.detectLoop = null;
            timersRef.current.vadLoop = null;

            try {
                const s = streamRef.current;
                if (s) s.getTracks().forEach(t => { try { t.stop(); } catch {} });
            } catch {}
            streamRef.current = null;

            try {
                const { ctx, src } = audioRef.current;
                if (src) src.disconnect();
                if (ctx && ctx.state !== 'closed') ctx.close();
            } catch {}
            audioRef.current = { ctx:null, src:null, analyser:null, timeBuf:null, freqBuf:null };
        };

        async function getStream(){
            const savedId = localStorage.getItem('exam.cameraId') || '';
            const base = { width:{ideal:1280}, height:{ideal:720}, frameRate:{ideal:24} };
            let constraints = savedId
                ? { video:{ ...base, deviceId:{ exact:savedId } }, audio:true }
                : { video:{ ...base, facingMode:{ ideal:'user' } }, audio:true };
            try{
                const s = await navigator.mediaDevices.getUserMedia(constraints);
                const usedV = s.getVideoTracks()[0]?.getSettings?.().deviceId;
                if (usedV) localStorage.setItem('exam.cameraId', usedV);
                return s;
            }catch(e){
                constraints = { video:{ ...base, facingMode:{ ideal:'user' } }, audio:true };
                return navigator.mediaDevices.getUserMedia(constraints);
            }
        }

        async function start(){
            // STREAM
            const stream = await getStream().catch(()=>null);
            if (!stream || cancelled) return;
            streamRef.current = stream;

            // WATCHERS: camera/mic
            const vT = stream.getVideoTracks()[0];
            const aT = stream.getAudioTracks()[0];
            if (vT){
                vT.onended  = ()=> sendFlag('camera_off');
                vT.onmute   = ()=> sendFlag('camera_off', { reason:'muted' });
                vT.onunmute = ()=> {};
            }
            if (aT){
                aT.onmute   = ()=> sendFlag('mic_muted', { reason:'muted' });
            }

            navigator.mediaDevices?.addEventListener?.('devicechange', async ()=>{
                try{
                    const all = await navigator.mediaDevices.enumerateDevices();
                    const vids = all.filter(d => d.kind === 'videoinput').map(d=>d.deviceId);
                    const cur  = localStorage.getItem('exam.cameraId') || '';
                    if (cur && !vids.includes(cur)){
                        await sendFlag('camera_switched', { prev: cur });
                    }
                }catch{}
            });

            // Visibility / blur
           /* const onVis  = ()=>{ if (document.hidden) sendFlag('alt_tab'); };
            const onBlur = ()=> sendFlag('window_blur');
            document.addEventListener('visibilitychange', onVis);
            window.addEventListener('blur', onBlur);*/

            // FaceDetector (native → fallback BlazeFace)
            if ('FaceDetector' in window){
                try{
                    faceDetector = new window.FaceDetector({ fastMode:true, maxDetectedFaces:3 });
                    useNative = true;
                }catch{}
            }
            if (!faceDetector){
                await ensureScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.14.0/dist/tf.min.js', 'tf');
                await ensureScript('https://cdn.jsdelivr.net/npm/tensorflow-blazeface-sync@0.1.3/dist/blazeface.min.js', 'blazeface');
                // @ts-ignore
                faceDetector = await window.blazeface.load();
                useNative = false;
            }

            // Offscreen <video> do detekcji
            const vEl = document.createElement('video');
            vEl.srcObject = stream; vEl.muted = true; vEl.playsInline = true;
            await new Promise((r)=>{
                const ready = () => { vEl.removeEventListener('loadedmetadata', ready); vEl.play().then(()=>r()).catch(()=>r()); };
                if (vEl.readyState >= 1) ready(); else vEl.addEventListener('loadedmetadata', ready, { once:true });
            });

            const faceLoop = async ()=>{
                if (cancelled) return;
                try{
                    const vt = stream.getVideoTracks()[0];
                    if (!vt || vt.readyState !== 'live') {
                        await sendFlag('camera_off', { reason:'track_not_live' });
                    } else {
                        let faceCount = 0;
                        if (useNative){
                            const faces = await faceDetector.detect(vEl);
                            faceCount = Array.isArray(faces) ? faces.length : 0;
                        } else {
                            // @ts-ignore
                            const preds = await faceDetector.estimateFaces(vEl, false);
                            faceCount = Array.isArray(preds) ? preds.length : 0;
                        }
                        if (faceCount === 0)      await sendFlag('face_missing');
                        else if (faceCount > 1)   await sendFlag('multi_face', { count: faceCount });
                    }
                    const at = stream.getAudioTracks()[0];
                    if (!at || at.muted || at.enabled === false || at.readyState !== 'live'){
                        await sendFlag('mic_muted', { muted: at?.muted ?? true, enabled: at?.enabled ?? false });
                    }
                }catch{}
                finally{ timersRef.current.detectLoop = setTimeout(faceLoop, faceIntervalMs); }
            };
            faceLoop();

            // VAD / NOISE
            try{
                const ctx  = new (window.AudioContext || window.webkitAudioContext)();
                const src  = ctx.createMediaStreamSource(stream);
                const an   = ctx.createAnalyser();
                an.fftSize = 2048;
                an.smoothingTimeConstant = 0.8;
                src.connect(an);
                audioRef.current = {
                    ctx, src, analyser: an,
                    timeBuf: new Float32Array(an.fftSize),
                    freqBuf: new Float32Array(an.frequencyBinCount),
                };

                calibRef.current = { done:false, endTs: performance.now() + noiseCalibMs, floorDb: -80 };
                noiseState.current  = { startTs:0, peakDb:-Infinity };
                speechState.current = { startTs:0 };

                const nyquist = ctx.sampleRate / 2;
                const [loHz, hiHz] = speechBandHz;
                const loBin = Math.max(0, Math.floor((loHz/nyquist) * an.frequencyBinCount));
                const hiBin = Math.min(an.frequencyBinCount-1, Math.ceil((hiHz/nyquist) * an.frequencyBinCount));

                const vadTick = ()=>{
                    if (cancelled) return;

                    const { analyser, timeBuf, freqBuf } = audioRef.current;
                    if (!analyser) return;

                    const aT = stream.getAudioTracks()[0];
                    if (!aT || aT.muted || aT.enabled === false || aT.readyState !== 'live'){
                        calibRef.current.done = false;
                        calibRef.current.endTs = performance.now() + noiseCalibMs;
                        timersRef.current.vadLoop = setTimeout(vadTick, vadPollMs);
                        return;
                    }

                    analyser.getFloatTimeDomainData(timeBuf);
                    let sumSq = 0;
                    for (let i=0;i<timeBuf.length;i++){ const x=timeBuf[i]; sumSq += x*x; }
                    const rms = Math.sqrt(sumSq / timeBuf.length);
                    const dB  = rmsToDb(rms);

                    const now = performance.now();
                    if (!calibRef.current.done){
                        const alpha = 0.1;
                        calibRef.current.floorDb = (1-alpha)*calibRef.current.floorDb + alpha*dB;
                        if (now >= calibRef.current.endTs) calibRef.current.done = true;
                        timersRef.current.vadLoop = setTimeout(vadTick, vadPollMs);
                        return;
                    }

                    const floor = calibRef.current.floorDb;
                    const thr   = floor + noiseMarginDb;
                    const loud  = dB >= thr;

                    analyser.getFloatFrequencyData(freqBuf);
                    let bandSum = 0, allSum = 0;
                    for (let i=0;i<freqBuf.length;i++){
                        const lin = Math.pow(10, freqBuf[i]/10);
                        allSum += lin;
                        if (i>=loBin && i<=hiBin) bandSum += lin;
                    }
                    const speechRatio = allSum>0 ? (bandSum/allSum) : 0;

                    let zc = 0;
                    for (let i=1;i<timeBuf.length;i++){
                        const a=timeBuf[i-1], b=timeBuf[i];
                        if ((a<=0 && b>0) || (a>=0 && b<0)) zc++;
                    }
                    const zcr = zc / timeBuf.length;

                    // NOISE
                    if (loud){
                        if (noiseState.current.startTs === 0){
                            noiseState.current.startTs = now;
                            noiseState.current.peakDb = dB;
                        } else {
                            noiseState.current.peakDb = Math.max(noiseState.current.peakDb, dB);
                        }
                        if (now - noiseState.current.startTs >= noiseMinHoldMs){
                            sendFlag('noise', {
                                peakDb: Math.round(noiseState.current.peakDb*10)/10,
                                floorDb: Math.round(floor*10)/10,
                                marginDb: noiseMarginDb
                            });
                            noiseState.current = { startTs:0, peakDb:-Infinity };
                        }
                    } else {
                        noiseState.current = { startTs:0, peakDb:-Infinity };
                    }

                    // CONVERSATION
                    const speechLike = (speechRatio > 0.45) && (zcr > 0.02 && zcr < 0.20) && loud;
                    if (speechLike){
                        if (speechState.current.startTs === 0) speechState.current.startTs = now;
                        if (now - speechState.current.startTs >= speechMinHoldMs){
                            sendFlag('conversation', {
                                speechRatio: Math.round(speechRatio*100)/100,
                                zcr: Math.round(zcr*1000)/1000,
                                levelDb: Math.round(dB*10)/10,
                                floorDb: Math.round(floor*10)/10
                            });
                            speechState.current.startTs = 0;
                        }
                    } else {
                        speechState.current.startTs = 0;
                    }

                    timersRef.current.vadLoop = setTimeout(vadTick, vadPollMs);
                };
                vadTick();
            }catch{
                // Brak AudioContext – pomijamy hałas/rozmowę (reszta flag działa)
            }

            // — cleanup listeners —
            /*return () => {
                document.removeEventListener('visibilitychange', onVis);
                window.removeEventListener('blur', onBlur);
            };*/
        }

        (async ()=>{ await start(); })();

        return ()=>{ cancelled = true; cleanup(); };
    }, [
        attemptId, enabled, faceIntervalMs, cooldowns,
        noiseCalibMs, noiseMarginDb, noiseMinHoldMs,
        speechBandHz[0], speechBandHz[1], speechMinHoldMs, vadPollMs
    ]);
}
